import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsCart3 } from 'react-icons/bs';
import { IoIosArrowDown } from 'react-icons/io';
import { TbShoppingCart } from 'react-icons/tb';
import { When } from 'react-if';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import Tooltip from '@/components/@base/tooltip';
import { Text } from '@/components/@base/typography';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import Section from '@/components/app/product/components/@shared/section';
import AffiliateCard from '@/components/app/product/components/affiliate-card';
import Highlights from '@/components/app/product/components/highlights';
import ReleaseDetails from '@/components/app/product/components/release-details';
import InstallationRulesButton from '@/components/app/product/components/side-bar/installation-rules-button';
import PurchaseHistory from '@/components/app/product/components/side-bar/purchase-history';
import RateAction from '@/components/app/product/components/side-bar/rate-action';
import Props from '@/components/app/product/components/side-bar/type';
import StoreCard from '@/components/app/product/components/store-card';
import { isBundle } from '@/components/app/product/helpers';
import {
  getSingleProductOptions,
  useGetAddOnsInfo,
} from '@/components/app/product/services/hooks';
import AddToCartButton from '@/components/shared/components/add-to-cart-button';
import OptionsItem from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item';
import OptionsLoading from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item/loading';
import { ADDONS_ENUM } from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/type';
import { isAddonDisabled } from '@/components/shared/components/add-to-cart-button/helper';
import { SequentialIcons } from '@/components/shared/components/icon';
import PriceView from '@/components/shared/components/price-view';

import { useAddonsStore } from '@/store/addons.store';
import { usePrerequisitesStore } from '@/store/prerequisites.store';

import {
  DATA_CY_AFFILIATE_CARD_PRODUCTS,
  DATA_CY_PRODUCT_SIDEBAR_CART,
} from '@/constant/cypress';
import ICON_SIZE, { ICON_COLOR } from '@/constant/icon-size-color';
import localization, { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;
const defaultHighlights = [
  productLocalization.defaultHighlight1,
  productLocalization.defaultHighlight2,
  productLocalization.defaultHighlight3,
];

const oneYearSubscriptionHighlight = [
  productLocalization.oneYearSubscriptionHighlight1,
  productLocalization.oneYearSubscriptionHighlight2,
  productLocalization.oneYearSubscriptionHighlight3,
  productLocalization.oneYearSubscriptionHighlight4,
  productLocalization.oneYearSubscriptionHighlight5,
];

const lifetimeSubscriptionHighlight = [
  productLocalization.lifetimeSubscriptionHighlight1,
  productLocalization.lifetimeSubscriptionHighlight2,
  productLocalization.lifetimeSubscriptionHighlight3,
  productLocalization.lifetimeSubscriptionHighlight4,
  productLocalization.lifetimeSubscriptionHighlight5,
];

export default function SideBar({ slug, className, stickySideBar }: Props) {
  const { addons, setAddons } = useAddonsStore();
  const { prerequisites, setPrerequisites } = usePrerequisitesStore();
  const [isSticky, setIsSticky] = useState(false);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const firstBoxRef = useRef<HTMLDivElement>(null);

  const [subscriptionLifetime, setSubscriptionLifetime] = useState(false);
  const [subscriptionOneYear, setSubscriptionOneYear] = useState(false);

  const { data: addonsData, isPending: isAddonsDataPending } = useGetAddOnsInfo({
    slug,
  });
  const { data } = useSuspenseQuery(getSingleProductOptions(slug));
  const {
    id,
    title,
    saleAmount,
    price,
    lastPrice,
    discount,
    isOnSale,
    saleEndedAt,
    medals,
    releaseDate,
    updateDate,
    version,
    rate,
    store,
    affiliatePrice,
    affiliateTermsUrl,
    sellable,
    isSubscription,
    subscriptionDefaultPriceIsOneYear,
    rated,
  } = data;
  let highlights = isSubscription
    ? subscriptionDefaultPriceIsOneYear
      ? oneYearSubscriptionHighlight
      : lifetimeSubscriptionHighlight
    : defaultHighlights;
  if (subscriptionLifetime) {
    highlights = lifetimeSubscriptionHighlight;
  }
  if (subscriptionOneYear) {
    highlights = oneYearSubscriptionHighlight;
  }
  const [open, setOpen] = useState(false);
  const [openSubscriptionAddons, setOpenSubscriptionAddons] = useState(true);
  const [disableTooltip, setDisableTooltip] = useState(false);
  const subscriptionDefaultSetRef = useRef(false);

  const [finalPrice, setFinalPrice] = useState(price);
  const [finalLastPrice, setFinalLastPrice] = useState(lastPrice);
  const finalDiscount = isOnSale ? discount : 0;
  const [displayFinalPrice, setDisplayFinalPrice] = useState(price);

  useEffect(() => {
    if (data) {
      let calculatedPrice = price;
      let calculatedLastPrice = lastPrice;
      if (addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR]?.selected) {
        const subscriptionItem = addonsData?.addons?.find(
          (item) => item.type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR,
        );
        calculatedLastPrice = subscriptionItem?.lastPrice ?? data.lastPrice;
        calculatedPrice =
          subscriptionItem?.price ?? addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR].price;
      }
      if (addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME]?.selected) {
        calculatedLastPrice = data.regularPrice;
        calculatedPrice = data.subscriptionDefaultPriceIsOneYear
          ? data.price + addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME]?.price
          : data.price;
      }
      if (addons[ADDONS_ENUM.REPEAT_PURCHASE]?.selected && lastPrice > 0) {
        calculatedPrice = lastPrice;
      }
      if (addons[ADDONS_ENUM.EXTRA_SUPPORT]?.selected) {
        calculatedLastPrice += addons[ADDONS_ENUM.EXTRA_SUPPORT]?.price || 0;
      }
      for (const [key, value] of Object.entries(addons)) {
        if (
          value.selected &&
          key !== ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR &&
          key !== ADDONS_ENUM.SUBSCRIPTION_LIFETIME
        ) {
          calculatedPrice += value.price;
        }
      }
      for (const prerequisite of Object.values(prerequisites)) {
        if (!prerequisite.selected) {
          calculatedPrice -= prerequisite.price;
          calculatedLastPrice -= prerequisite.price;
        }
      }

      setFinalPrice(calculatedPrice);
      setFinalLastPrice(calculatedLastPrice);
    }
  }, [data, addons, prerequisites]);
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      if (sidebarRef.current && firstBoxRef.current) {
        const sidebarBottom = sidebarRef.current.getBoundingClientRect().bottom;
        const firstBoxHeight = firstBoxRef.current.offsetHeight + window.innerHeight;
        if (stickySideBar) {
          if (window.scrollY + window.innerHeight >= sidebarBottom + firstBoxHeight + 1100) {
            setIsSticky(true);
          } else if (window.scrollY < firstBoxHeight) {
            setIsSticky(false);
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stickySideBar]);
  const handleAddOnsChange = (key: string, checked: boolean, price: number) => {
    setAddons({
      ...addons,
      [key]: { selected: checked, price: price },
    });
    if (key === ADDONS_ENUM.SUBSCRIPTION_LIFETIME) {
      setSubscriptionLifetime(checked);
    }
    if (key === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR) {
      setSubscriptionOneYear(checked);
    }
  };
  const hasSelectedAddon = Object.values(addons).some((addon) => addon.selected);
  useEffect(() => {
    if (hasSelectedAddon) {
      setDisplayFinalPrice(finalPrice);
    } else {
      const timeout = setTimeout(() => {
        setDisplayFinalPrice(finalPrice);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [finalPrice, hasSelectedAddon]);

  const handlePrerequisitesChange = (id: string, checked: boolean, price: number) => {
    setPrerequisites({
      ...prerequisites,
      [id]: { selected: checked, price: price },
    });
  };

  const calculateMedalsWidth = (count) => {
    //calculate the max width of sequential medals, assuming the maximum number of medals is 5
    const fullWidth = 135;
    const maxNumberOfMedals = 5;
    return (fullWidth * count) / maxNumberOfMedals;
  };
  const subscriptionTypes = [
    ADDONS_ENUM.SUBSCRIPTION_LIFETIME,
    ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR,
  ];

  const subscriptionAddonsDisplay = useMemo(() => {
    if (!data || !isSubscription) return [];
    const fromApi = addonsData?.addons?.filter(({ type }) =>
      subscriptionTypes.includes(type as ADDONS_ENUM),
    );
    const lifetimeFromApi = fromApi?.find((a) => a.type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME);
    const oneYearFromApi = fromApi?.find((a) => a.type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR);
    const oneYearItem = oneYearFromApi
      ? {
          type: ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR as const,
          title: productLocalization.oneYear,
          subTitle: productLocalization.oneYearSubscriptionHighlight1,
          price: oneYearFromApi.price ?? 0,
          lastPrice: oneYearFromApi.lastPrice ?? data.lastPrice,
          discount: oneYearFromApi.discount ?? 0,
        }
      : {
          type: ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR as const,
          title: productLocalization.oneYear,
          subTitle: productLocalization.oneYearSubscriptionHighlight1,
          price: data.price ?? 0,
          lastPrice: data.lastPrice,
          discount: data?.discount ?? 0,
        };
    const lifetimeItem = lifetimeFromApi
      ? {
          type: ADDONS_ENUM.SUBSCRIPTION_LIFETIME as const,
          title: productLocalization.lifetime,
          subTitle: productLocalization.lifetimeSubscriptionHighlight1,
          price: lifetimeFromApi.price,
          lastPrice: lifetimeFromApi.lastPrice ?? data.lastPrice ?? 0,
          discount: lifetimeFromApi.discount ?? 0,
        }
      : {
          type: ADDONS_ENUM.SUBSCRIPTION_LIFETIME as const,
          title: productLocalization.lifetime,
          subTitle: productLocalization.lifetimeSubscriptionHighlight1,
          price: data.regularPrice ? data.price : 0,
          lastPrice: data.lastPrice,
          discount: data?.discount ?? 0,
        };

    return [oneYearItem, lifetimeItem].filter((item) => item.price !== 0);
  }, [data, isSubscription, addonsData?.addons]);

  const handleSubscriptionChange = (
    type: ADDONS_ENUM.SUBSCRIPTION_LIFETIME | ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR,
    checked: boolean,
    price: number,
  ) => {
    if (!checked) {
      return;
    }
    const other =
      type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME
        ? ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR
        : ADDONS_ENUM.SUBSCRIPTION_LIFETIME;
    const updated = {
      ...addons,
      [other]: { selected: false, price: addons[other]?.price ?? 0 },
    };
    updated[type] = { selected: true, price };
    setAddons(updated);
    setSubscriptionLifetime(type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME);
    setSubscriptionOneYear(type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR);
  };

  useEffect(() => {
    if (!data || !isSubscription || subscriptionAddonsDisplay.length === 0) return;
    subscriptionDefaultSetRef.current = false;
  }, [data?.id]);

  useEffect(() => {
    if (!data || !isSubscription) return;
    const hasLifetime = addons[ADDONS_ENUM.SUBSCRIPTION_LIFETIME]?.selected;
    const hasOneYear = addons[ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR]?.selected;
    if (hasLifetime || hasOneYear) return;
    let defaultType;
    if (subscriptionAddonsDisplay.length === 1) {
      defaultType = subscriptionAddonsDisplay[0].type;
    } else {
      const defaultOneYear = subscriptionDefaultPriceIsOneYear ?? true;
      defaultType = defaultOneYear
        ? ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR
        : ADDONS_ENUM.SUBSCRIPTION_LIFETIME;
    }
    const item = subscriptionAddonsDisplay.find((a) => a.type === defaultType);

    if (!item && subscriptionAddonsDisplay.length > 0) {
      defaultType = subscriptionAddonsDisplay[0].type;
    }

    if (!item && subscriptionAddonsDisplay.length > 0) {
      const fallbackItem = subscriptionAddonsDisplay.find((a) => a.type === defaultType);
      if (!fallbackItem) return;
    }
    if (!item) return;

    subscriptionDefaultSetRef.current = true;
    setAddons({
      ...addons,
      [defaultType]: { selected: true, price: item.price },
    });
    setSubscriptionLifetime(defaultType === ADDONS_ENUM.SUBSCRIPTION_LIFETIME);
    setSubscriptionOneYear(defaultType === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR);
  }, [
    data?.id,
    isSubscription,
    subscriptionDefaultPriceIsOneYear,
    subscriptionAddonsDisplay,
    addons,
  ]);

  const normalAddons = addonsData?.addons?.filter(
    ({ type }) => !subscriptionTypes.includes(type as ADDONS_ENUM),
  );
  const renderSidebarContent = (options?: { showRateAction: boolean }) => (
    <>
      <Container center className='justify-between'>
        <Container className='flex flex-col gap-0.5'>
          <BsCart3 fill='#FFC107' size={23} />
          <Container center className='gap-1'>
            <Text type={TEXT_TYPE.SPAN} className='text-[22px] font-bold text-[#424244]'>
              {saleAmount}
            </Text>
            <Text type={TEXT_TYPE.SPAN} className='text-sm text-[#76767C]'>
              {localization.sale}
            </Text>
          </Container>
        </Container>
        <PriceView
          sellable={sellable}
          price={finalPrice}
          lastPrice={finalLastPrice}
          discount={finalDiscount}
          saleEndedAt={saleEndedAt}
          showDiscountWithoutExpiration={isBundle(data)}
          className='relative flex h-full items-center gap-3 rounded-s-md bg-[linear-gradient(111.32deg,_#FFDAA266_-1.54%,_#FFF3E066_21.68%)] px-6 py-5 md:-ml-6'
          priceWrapperClassName='flex flex-col items-end'
          priceClassName={cn(
            'text-[22px]! md:text-[25px]! font-bold',
            !finalDiscount && 'flex-col',
          )}
          discountLabelClassName='absolute md:static inset-x-0 -top-6 mx-auto border-[#FFFFFF] border-[2px] shadow-[0px_4px_12px_-5px_#FF7B016E]'
          priceCurrencyWrapperClassName='flex-col items-end gap-0'
          currencyClassName='text-[#BFBFBF]'
          regularPrice={data.regularPrice}
        />
      </Container>
      <Highlights highlights={highlights} noHeader className='border-none!' />

      {sellable && isAddonsDataPending ? (
        <Container className='flex flex-col divide-y divide-dashed divide-[#7E899B33] rounded-md border-[1px] border-[#E5E8EB] px-3'>
          <OptionsLoading hasCheckbox />
          <OptionsLoading hasCheckbox />
        </Container>
      ) : (
        sellable &&
        addonsData &&
        addonsData.addons.length > 0 && (
          <>
            {subscriptionAddonsDisplay.length > 0 && (
              <Container
                className={cn(
                  'relative flex flex-col rounded-md border-[1px] border-[#E5E8EB] px-3 pt-2.5',
                  openSubscriptionAddons ? 'pb-0' : 'pb-1.5',
                )}
              >
                <Container
                  className='mb-1 flex cursor-pointer items-center justify-between'
                  onClick={() => {
                    setOpenSubscriptionAddons((prev) => !prev);
                    setDisableTooltip(true);
                  }}
                >
                  <Container className='w-[85%]' onMouseEnter={() => setDisableTooltip(false)}>
                    <Text className='text-sm leading-[35px] text-[#76767C]'>
                      {productLocalization.subscriptionDuration}
                    </Text>
                  </Container>
                  <Container
                    onMouseEnter={() => setDisableTooltip(false)}
                    className='flex h-8 w-8 items-center justify-center rounded-md border'
                  >
                    <IoIosArrowDown
                      className={cn(
                        'text-[#878F9B] transition duration-300',
                        openSubscriptionAddons ? 'rotate-180' : 'rotate-0',
                      )}
                    />
                  </Container>
                </Container>

                {openSubscriptionAddons && (
                  <Container className='-mt-1.5 flex flex-col divide-y divide-dashed divide-[#7E899B33]'>
                    {subscriptionAddonsDisplay.map(
                      ({ type, title, price, lastPrice, discount }) => (
                        <OptionsItem
                          key={type}
                          typeItem={type}
                          labelClassName='max-w-[240px] text-[#76767C]! '
                          label={title}
                          price={
                            type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME
                              ? data.regularPrice
                              : price
                          }
                          hasCheckbox
                          onChange={(checked) =>
                            handleSubscriptionChange(type, checked, price)
                          }
                          checked={addons[type]?.selected}
                          disabled={false}
                          additionalItems={null}
                          subTitle={
                            type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME
                              ? productLocalization.subscriptionLifetimeHint
                              : productLocalization.subscriptionOneYearHint
                          }
                          showCurrency
                          noSign={
                            type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME ||
                            type === ADDONS_ENUM.SUBSCRIPTION_ONE_YEAR
                          }
                          finalLastPrice={
                            type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME &&
                            data.subscriptionDefaultPriceIsOneYear &&
                            finalDiscount > 0
                              ? price + data.price
                              : type === ADDONS_ENUM.SUBSCRIPTION_LIFETIME && finalDiscount > 0
                                ? price
                                : 0
                          }
                          discount={discount || 0}
                          lastPrice={lastPrice || 0}
                          className='py-3'
                          priceClassName={`ms-auto -mb-1.5 ${discount ? 'pt-0.5' : ''}`}
                          currencyClassName='text-[14px]!'
                          lastPriceClassName={`${price > 0 && 'pl-10!'}`}
                          showCircle={true}
                          groupClassName='mt-2!'
                        />
                      ),
                    )}
                  </Container>
                )}
              </Container>
            )}
            {normalAddons && normalAddons.length > 0 && (
              <Container className='flex flex-col rounded-md border-[1px] border-[#E5E8EB] px-2 pb-2'>
                <Container
                  id='addons'
                  className='flex flex-col divide-y divide-dashed divide-[#7E899B33] pr-[7px]'
                >
                  {normalAddons.map((addon) => {
                    const { type, title, price, subTitle, lastPrice, discount, excludes } =
                      addon;
                    const isDisabled = isAddonDisabled(type, excludes, addons, normalAddons);

                    return (
                      <OptionsItem
                        key={type}
                        // type={type}
                        typeItem={type}
                        labelClassName='max-w-[240px] line-clamp-2!'
                        label={title}
                        price={price}
                        hasCheckbox
                        onChange={(checked) => handleAddOnsChange(type, checked, price)}
                        checked={addons[type]?.selected}
                        disabled={isDisabled}
                        additionalItems={
                          type === ADDONS_ENUM.INSTALLATION && <InstallationRulesButton />
                        }
                        subTitle={subTitle}
                        showCurrency
                        finalLastPrice={0}
                        discount={discount || 0}
                        lastPrice={lastPrice || 0}
                        className='flex-col items-start'
                        priceClassName={`ms-auto -mb-2 ${discount ? 'pt-0.5' : ''}`}
                        currencyClassName='text-[14px]!'
                        lastPriceClassName={`${price > 0 && 'pl-10!'}`}
                      />
                    );
                  })}
                </Container>

                {/* ------------------ Final Price ------------------ */}
                <Container
                  className={clsx(
                    'mt-1 grid transition-[grid-template-rows] duration-500 ease-in-out',
                    hasSelectedAddon ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <Container className='overflow-hidden'>
                    <Container className='px-0 pb-0'>
                      <OptionsItem
                        className='h-[61px] rounded-[7px] bg-[linear-gradient(180deg,#84ba521a_0%,rgba(132,186,82,0)_100%)] px-2 py-[13px]'
                        label={productLocalization.payableAmount}
                        labelClassName='text-[#76767C]!'
                        finalLastPriceClassName='text-[#424244]! font-600 text-xl!'
                        price={displayFinalPrice}
                        noSign
                      />
                    </Container>
                  </Container>
                </Container>
              </Container>
            )}
          </>
        )
      )}

      {data?.prerequisites?.length > 0 && (
        <>
          <Container className='relative flex flex-col rounded-md border-[1px] border-[#E5E8EB] px-3 py-3'>
            <Tooltip
              className='tooltip-arrow-left -top-11! -left-0.5!'
              place='top-start'
              id='prerequisiteTooltip'
            />
            <Container
              {...(!disableTooltip && {
                'data-tooltip-id': 'prerequisiteTooltip',
                'data-tooltip-content': productLocalization.prerequisiteTooltip,
              })}
              className='flex cursor-pointer items-center justify-between'
              onClick={() => {
                setOpen((prev) => !prev);
                setDisableTooltip(true);
              }}
            >
              <Container className='w-[85%]' onMouseEnter={() => setDisableTooltip(false)}>
                <Text className='text-sm leading-[35px] text-[#76767C]'>
                  {productLocalization.prerequisiteProducts}
                </Text>
              </Container>
              <Container
                onMouseEnter={() => setDisableTooltip(false)}
                className='flex h-8 w-8 items-center justify-center rounded-md border'
              >
                <IoIosArrowDown
                  className={cn(
                    'text-[#878F9B] transition duration-300',
                    open ? 'rotate-180' : 'rotate-0',
                  )}
                />
              </Container>
            </Container>

            {open && (
              <Container className='mt-2 flex flex-col'>
                {data.prerequisites.map(({ id, title, price }) => (
                  <OptionsItem
                    className='py-1.5'
                    key={id}
                    label={title}
                    price={price}
                    checked={prerequisites[id]?.selected !== false}
                    onChange={(checked) => handlePrerequisitesChange(id, checked, price)}
                    hasCheckbox
                    noSign
                  />
                ))}
              </Container>
            )}
          </Container>
        </>
      )}

      {sellable && (
        <AddToCartButton
          isFree={price === 0 && normalAddons?.length === 0}
          productId={id}
          Icon={<TbShoppingCart color={ICON_COLOR.white} size={ICON_SIZE.sm} />}
          slug={slug}
          className='h-[52px] w-full hover:bg-[#EB8800]'
        >
          <Text className='text-[15px]! font-bold'>
            {isSubscription
              ? subscriptionLifetime
                ? productLocalization.GetLifetimeSubscription
                : subscriptionOneYear
                  ? productLocalization.GetOneYearSubscription
                  : subscriptionDefaultPriceIsOneYear
                    ? productLocalization.GetOneYearSubscription
                    : productLocalization.GetLifetimeSubscription
              : localization.addToCart}
          </Text>
        </AddToCartButton>
      )}

      {medals && medals.length > 0 && (
        <Container
          className='w-full max-w-[135px] self-center'
          style={{ maxWidth: `${calculateMedalsWidth(medals.length)}px` }}
        >
          <SequentialIcons
            icons={medals}
            size={36}
            iconClassName='absolute h-10 w-10 rounded-full border-none'
            wrapperClassName='relative h-10 border-b-[1px] border-b-[#EDEEF2]'
            shiftAmount={25}
          />
        </Container>
      )}
      <ReleaseDetails
        releaseDate={releaseDate}
        version={version}
        updateDate={updateDate}
        rate={rate}
      />
      {options?.showRateAction && <RateAction productId={id} slug={slug} />}
    </>
  );

  return (
    <aside data-cy={DATA_CY_PRODUCT_SIDEBAR_CART} className={cn('', className)}>
      <Container ref={sidebarRef}>
        <Container ref={firstBoxRef}>
          <Section center className='w-full flex-col items-stretch gap-4 px-6! py-7!'>
            {renderSidebarContent({ showRateAction: true })}
          </Section>
        </Container>
        <When condition={store?.id}>
          <StoreCard {...store} />
        </When>
        {sellable && price > 0 && (
          <AffiliateCard
            data-cy={DATA_CY_AFFILIATE_CARD_PRODUCTS}
            slug={slug}
            price={affiliatePrice}
            termsUrl={affiliateTermsUrl}
            productName={title}
          />
        )}
        {price !== 0 && <PurchaseHistory slug={slug} productId={id} />}
      </Container>
      {stickySideBar && (
        <Section
          center
          className={cn(
            'sticky top-[10%] w-full max-w-[350px] flex-col items-stretch gap-4 px-6! py-7! opacity-0 transition duration-300',
            isSticky && 'md:opacity-100',
          )}
        >
          {renderSidebarContent()}
        </Section>
      )}
    </aside>
  );
}
